/* ===== CSS CUSTOM PROPERTIES ===== */
:root {
    /* Color System */
    --color-primary: #54535C;
    --color-primary-light: rgba(84, 83, 92, 0.2);
    --color-primary-medium: #54535c91;
    --color-border: #e0e0e0;
    --color-surface: #ffffff;

    /* Responsive Breakpoints */
    --breakpoint-xs: 320px;
    --breakpoint-sm: 480px;
    --breakpoint-md: 768px;
    --breakpoint-lg: 1024px;
    --breakpoint-xl: 1200px;

    /* Fluid Spacing System */
    --spacing-xs: clamp(2px, 0.5vw, 4px);
    --spacing-sm: clamp(4px, 1vw, 8px);
    --spacing-md: clamp(8px, 1.5vw, 12px);
    --spacing-lg: clamp(12px, 2vw, 16px);
    --spacing-xl: clamp(0px, 2.5vw, 0px);
    --spacing-2xl: clamp(20px, 3vw, 24px);

    /* Typography Scale */
    --font-size-xs: clamp(10px, 2vw, 12px);
    --font-size-sm: clamp(11px, 2.2vw, 14px);
    --font-size-md: clamp(12px, 2.5vw, 12px);
    --font-size-lg: clamp(14px, 2.8vw, 18px);
    --font-size-xl: clamp(16px, 3vw, 20px);
    --font-weight-normal: 400;
    --font-weight-medium: 500;
    --font-weight-semibold: 600;
    --font-weight-bold: 700;

    /* Component Sizes */
    --connector-size: clamp(20px, 5vw, 80px);
    --circle-size: clamp(24px, 6vw, 50px);
    --dot-size: clamp(10px, 3vw, 10px);
    --touch-target-min: 44px;
    --touch-target-small: 0px;

    /* Timeline Variables */
    --timeline-line-height: clamp(2px, 0.3vw, 2px);
    --timeline-line-color: #7c7c7cab;

    /* Shadows & Effects */
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
    --shadow-md: 0 2px 4px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 4px 6px rgba(0, 0, 0, 0.1);
    --shadow-xl: 0 8px 25px rgba(0, 0, 0, 0.15);

    /* Border Radius */
    --border-radius-sm: clamp(2px, 0.5vw, 4px);
    --border-radius: clamp(4px, 1vw, 8px);
    --border-radius-lg: clamp(8px, 1.5vw, 12px);
    --border-radius-xl: clamp(12px, 2vw, 16px);

    /* Transitions */
    --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 400ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* ===== MAIN CONTAINER ===== */
.installments.payment-progress {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: min(700px, 95vw);
    padding: var(--spacing-lg);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.5;
    transition: all var(--transition-normal);
    margin: 0 auto;
}

.installments.payment-progress.bordered {
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow-md);
    padding: var(--spacing-xl);
    background-color: var(--color-surface);
}

/* ===== SIMPLE TIMELINE LAYOUT ===== */
.installments.payment-progress.date-timeline {
    flex-direction: column;
    background-color: var(--color-surface);
    gap: var(--spacing-lg);
    align-items: stretch;
    position: relative;
}

.installments.payment-progress.date-timeline.bordered {
    border: 1px solid var(--color-border);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-lg);
}

.date-timeline-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    position: relative;
    gap: var(--spacing-md);
    min-height: 60px;
    z-index: 0;
}

/* ===== SIMPLE DOT STYLE ===== */
.modern-timeline-dot {
    width: var(--dot-size);
    height: var(--dot-size);
    background-color: var(--color-primary);
    border-radius: 50%;
    position: relative;
    margin-bottom: var(--spacing-sm);
    z-index: 10;
    transition: all var(--transition-normal);
    min-width: var(--dot-size);
    min-height: var(--dot-size);
    border: 2px solid var(--color-primary);
}

.modern-timeline-dot:hover {
    transform: scale(1.1);
}

/* All states use the same simple circular design */
.modern-timeline-dot.empty,
.modern-timeline-dot.small,
.modern-timeline-dot.medium,
.modern-timeline-dot.full {
    background-color: var(--color-primary);
    border: 2px solid var(--color-primary);
}

/* ===== SIMPLE TIMELINE CONNECTING LINES ===== */
/* Default solid line for simple timeline (when no hidden installments) */
.date-timeline-container::before {
    content: '';
    position: absolute;
    top: calc(var(--dot-size) / 1.5 - var(--timeline-line-height) / 2);
    left: 12.5%;
    right: 12.5%;
    height: var(--timeline-line-height);
    background-color: var(--color-primary);
    z-index: 1;
    transition: all var(--transition-normal);
}

/* Default: hide the after pseudo-element */
.date-timeline-container::after {
    display: none;
}

/* When there are hidden installments, replace with segmented line */
.date-timeline.has-hidden-installments .date-timeline-container::before {
    display: none;
}

.date-timeline.has-hidden-installments .date-timeline-container::after {
    content: '';
    display: block;
    position: absolute;
    top: calc(var(--dot-size) / 1 - var(--timeline-line-height) / 2);
    left: 12.5%;
    right: 12.5%;
    height: var(--timeline-line-height);
    z-index: 1;
    transition: all var(--transition-normal);
    background:
            linear-gradient(to right, var(--color-primary), var(--color-primary)) 0% 0% / 66.66% 100% no-repeat,
            repeating-linear-gradient(
                    to right,
                    var(--timeline-line-color) 0%,
                    var(--timeline-line-color) 4px,
                    transparent 4px,
                    transparent 8px
            ) 66.66% 0% / 100% 100% no-repeat;
}

[dir="rtl"] .date-timeline-container::before {
    left: 12.5%;
    right: 12.5%;
    background-color: var(--color-primary);
}

[dir="rtl"] .date-timeline-container::after {
    display: none;
}

[dir="rtl"] .date-timeline.has-hidden-installments .date-timeline-container::before {
    display: none;
}

[dir="rtl"] .date-timeline.has-hidden-installments .date-timeline-container::after, .date-timeline-container-rtl::after{
    display: block !important;
    content: '' !important;
    position: absolute !important;
    top: calc(var(--dot-size) / 1 - var(--timeline-line-height) / 2) !important;
    left: 12.5% !important;
    right: 12.5% !important;
    height: var(--timeline-line-height) !important;
    z-index: 1 !important;
    transition: all var(--transition-normal) !important;
    background:
            repeating-linear-gradient(
                    to right,
                    var(--timeline-line-color) 0%,
                    var(--timeline-line-color) 3px,
                    transparent 3px,
                    transparent 6px
            ) 0% 0% / 32.67% 100% no-repeat,
            linear-gradient(to right, var(--color-primary), var(--color-primary)) 100% 0% / 66.66% 100% no-repeat !important;
}

[dir="rtl"] .date-timeline-container {
    direction: rtl;
}

[dir="rtl"] .date-timeline-content {
    text-align: center;
    direction: rtl;
}

[dir="rtl"] .date-timeline-footer {
    text-align: right;
    direction: rtl;
    border-left: none;
    border-right: 3px solid var(--color-accent);
}

[dir="rtl"] .currency-symbol {
    margin-right: 0;
    margin-left: 2px;
}

[dir="rtl"] .sar-symbol {
    margin-right: 0;
    margin-left: 3px;
}

/* ===== TIMELINE STEPS ===== */
.date-timeline-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 1;
    min-width: 0;
    transition: transform var(--transition-fast);
    min-height: var(--touch-target-min);
    padding: var(--spacing-xs);
}

/* ===== TIMELINE CONTENT ===== */
.date-timeline-content {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    min-height: clamp(40px, 10vw, 70px);
    justify-content: flex-start;
    width: 100%;
    overflow-wrap: break-word;
}

.date-timeline-date,
.date-timeline-label {
    font-size: var(--font-size-sm);
    color: var(--color-primary);
    font-weight: var(--font-weight-medium);
    line-height: 1.3;
    transition: color var(--transition-fast);
    overflow-wrap: break-word;
}

.date-timeline-amount {
    font-size: var(--font-size-md);
    color: var(--color-primary);
    font-weight: var(--font-weight-semibold);
    transition: color var(--transition-fast);
    white-space: nowrap;
}

.date-timeline-hold-label {
    font-size: var(--font-size-xs);
    color: var(--color-primary);
    font-weight: var(--font-weight-medium);
    opacity: 0.7;
    margin-top: var(--spacing-xs);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, var(--color-primary-light), transparent);
    padding: 2px 6px;
    border-radius: var(--border-radius-sm);
    transition: all var(--transition-fast);
}

.date-timeline-footer {
    text-align: left;
    font-size: var(--font-size-sm);
    color: var(--color-primary);
    padding: var(--spacing-md);
    background: linear-gradient(135deg, rgba(243, 244, 246, 0.8), rgba(249, 250, 251, 0.4));
    border-radius: var(--border-radius);
    border-left: 3px solid var(--color-accent);
    font-style: italic;
    line-height: 1.4;
    overflow-wrap: break-word;
    backdrop-filter: blur(10px);
}

/* ===== DEFAULT TIMELINE STYLES (CIRCULAR PROGRESS) ===== */
.installments-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    flex: 0 0 auto;
    transition: transform var(--transition-fast);
    min-height: var(--touch-target-min);
    padding: var(--spacing-sm);
}

.installments-step:hover {
    transform: translateY(-2px);
}

.installments-amount {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: var(--spacing-md);
    text-align: center;
}

.amount-container {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
}

.installments-label {
    font-size: var(--font-size-md);
    color: var(--color-primary);
    font-weight: var(--font-weight-normal);
    text-align: center;
    margin-top: var(--spacing-sm);
    transition: color var(--transition-fast);
    overflow-wrap: break-word;
}

.installments-amount-value {
    font-weight: var(--font-weight-bold);
    color: var(--color-primary);
    font-size: var(--font-size-lg);
    transition: color var(--transition-fast);
    white-space: nowrap;
}

.currency-symbol {
    margin-right: 2px;
}

.sar-symbol {
    width: clamp(14px, 3vw, 16px);
    height: clamp(14px, 3vw, 16px);
    display: inline-block;
    fill: var(--color-primary);
    margin-right: 3px;
    vertical-align: middle;
    transition: fill var(--transition-fast);
}

/* ===== CIRCLE PROGRESS STYLES ===== */
.installments-circle-container {
    position: relative;
    width: var(--circle-size);
    height: var(--circle-size);
    margin-bottom: var(--spacing-md);
    transition: transform var(--transition-normal);
    min-width: var(--circle-size);
    min-height: var(--circle-size);
}

.installments-circle-container:hover {
    transform: scale(1.05);
}

.installments-circle-outline,
.installments-circle-filled,
.installments-circle-filled-first,
.installments-circle-segment {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    transition: all var(--transition-normal);
}

.installments-circle-outline {
    background-color: var(--color-primary-light);
    border: 2px solid var(--color-border);
}

.installments-circle-filled-first {
    background-color: var(--color-primary-medium);
    border: 2px solid var(--color-primary);
    box-shadow: var(--shadow-sm);
}

.installments-circle-filled {
    background-color: var(--color-primary);
    border: 2px solid var(--color-primary);
    box-shadow: var(--shadow-md);
    position: relative;
}

.installments-circle-filled::after {
    content: '✓';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: clamp(12px, 3vw, 18px);
    font-weight: var(--font-weight-bold);
}

.installments-circle-segment-small {
    clip-path: polygon(50% 50%, 50% 0%, 25% 0%, 15% 15%, 50% 50%);
    background-color: var(--color-primary);
    transition: clip-path var(--transition-normal);
}

.installments-circle-segment-medium {
    clip-path: polygon(50% 50%, 50% 0%, 0% 0%, 0% 50%, 50% 50%);
    background-color: var(--color-primary);
    transition: clip-path var(--transition-normal);
}

/* ===== CONNECTOR STYLES ===== */
.installments-connector-container {
    flex-grow: 1;
    display: flex;
    align-items: center;
    position: relative;
    margin: 0 var(--spacing-sm);
    max-width: var(--connector-size);
    transition: opacity var(--transition-fast);
    min-width: 20px;
}

.installments-connector-container:hover {
    opacity: 0.8;
}

.installments-simple-connector,
.installments-dotted-connector {
    display: flex;
    align-items: center;
    width: 100%;
}

.installments-connector-dot {
    width: clamp(6px, 1.5vw, 8px);
    height: clamp(6px, 1.5vw, 8px);
    background-color: var(--color-primary);
    border-radius: 50%;
    transition: all var(--transition-fast);
    box-shadow: var(--shadow-sm);
    min-width: 6px;
    min-height: 6px;
}

.installments-connector-dot:hover {
    transform: scale(1.2);
    box-shadow: var(--shadow-md);
}

.installments-connector-line {
    flex-grow: 1;
    height: 2px;
    background-color: var(--color-primary);
    margin: 0 2px;
    transition: background-color var(--transition-fast);
    min-width: 10px;
}

.installments-dots-container {
    flex-grow: 1;
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    gap: 2px;
    min-width: 20px;
}

.installments-dot {
    width: clamp(4px, 1vw, 6px);
    height: clamp(4px, 1vw, 6px);
    background-color: var(--color-primary);
    border-radius: 50%;
    margin: 0 1px;
    transition: all var(--transition-fast);
    animation: pulse 2s infinite;
    min-width: 4px;
    min-height: 4px;
}

.installments-dot:nth-child(2) { animation-delay: 0.2s; }
.installments-dot:nth-child(3) { animation-delay: 0.4s; }
.installments-dot:nth-child(4) { animation-delay: 0.6s; }
.installments-dot:nth-child(5) { animation-delay: 0.8s; }

@keyframes pulse {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.1); }
}

/* ===== MOBILE OPTIMIZATION FOR DOTTED LINES ===== */
/* Mobile optimization for dotted lines - LTR */
@media (max-width: 479px) {
    .date-timeline.has-hidden-installments .date-timeline-container::after {
        background:
                linear-gradient(to right, var(--color-primary), var(--color-primary)) 0% 0% / 66.66% 100% no-repeat,
                repeating-linear-gradient(
                        to right,
                        var(--timeline-line-color) 0%,
                        var(--timeline-line-color) 2px,
                        transparent 2px,
                        transparent 4px
                ) 66.66% 0% / 100% 100% no-repeat;
    }

    /* Mobile optimization for dotted lines - RTL - HIGH SPECIFICITY */
    div[dir="rtl"] .installments.payment-progress.date-timeline.has-hidden-installments .date-timeline-container::after {
        background:
                repeating-linear-gradient(
                        to right,
                        var(--timeline-line-color) 0%,
                        var(--timeline-line-color) 2px,
                        transparent 2px,
                        transparent 4px
                ) 0% 0% / 33.34% 100% no-repeat,
                linear-gradient(to right, var(--color-primary), var(--color-primary)) 33.34% 0% / 66.66% 100% no-repeat !important;
    }
}

@media (min-width: 480px) and (max-width: 767px) {
    .date-timeline.has-hidden-installments .date-timeline-container::after {
        background:
                linear-gradient(to right, var(--color-primary), var(--color-primary)) 0% 0% / 66.66% 100% no-repeat,
                repeating-linear-gradient(
                        to right,
                        var(--timeline-line-color) 0%,
                        var(--timeline-line-color) 3px,
                        transparent 3px,
                        transparent 6px
                ) 66.66% 0% / 100% 100% no-repeat;
    }

    /* Tablet optimization for dotted lines - RTL - HIGH SPECIFICITY */
    div[dir="rtl"] .installments.payment-progress.date-timeline.has-hidden-installments .date-timeline-container::after {
        background:
                repeating-linear-gradient(
                        to right,
                        var(--timeline-line-color) 0%,
                        var(--timeline-line-color) 3px,
                        transparent 3px,
                        transparent 6px
                ) 0% 0% / 33.34% 100% no-repeat,
                linear-gradient(to right, var(--color-primary), var(--color-primary)) 33.34% 0% / 66.66% 100% no-repeat !important;
    }
}

/* Subtle animation for the dotted section (optional enhancement) */
@keyframes dotted-flow {
    0% {
        background-position: 0% 0%, 66.66% 0%;
    }
    100% {
        background-position: 0% 0%, 68% 0%;
    }
}

.date-timeline.has-hidden-installments .date-timeline-container::after {
    animation: dotted-flow 3s ease-in-out infinite alternate;
}

/* ===== RESPONSIVE DESIGN ===== */

/* Extra Small Mobile (320px - 479px) */
@media (max-width: 479px) {
    :root {
        --circle-size: clamp(18px, 8vw, 25px);
        --connector-size: clamp(8px, 4vw, 15px);
        --dot-size: clamp(10px, 4vw, 10px);
        --timeline-line-height: 2px;
    }

    .installments.payment-progress {
        padding: var(--spacing-sm);
        max-width: 100%;
    }

    .installments.payment-progress:not(.date-timeline) {
        flex-direction: row;
        overflow-x: auto;
        justify-content: flex-start;
        padding: var(--spacing-sm) var(--spacing-xs);
        gap: var(--spacing-xs);
        scrollbar-width: thin;
        scrollbar-color: var(--color-primary-light) transparent;
        -webkit-overflow-scrolling: touch;
        scroll-behavior: smooth;
    }

    .date-timeline-container {
        gap: var(--spacing-xs);
        min-height: 45px;
    }

    .date-timeline-step {
        min-width: clamp(35px, 8vw, 50px);
        padding: var(--spacing-xs);
    }

    .date-timeline-content {
        min-height: clamp(30px, 8vw, 40px);
        gap: 2px;
    }

    .date-timeline-date {
        font-size: var(--font-size-xs);
        line-height: 1.2;
        /* Hide original text content on mobile */
        font-size: 0;
        line-height: 0;
    }

    .date-timeline-date::before {
        /* Show data-short attribute value instead */
        content: attr(data-short);
        font-size: var(--font-size-xs);
        line-height: 1.2;
        color: var(--color-primary);
        font-weight: var(--font-weight-medium);
    }

    .date-timeline-amount {
        font-size: var(--font-size-xs);
        line-height: 1.2;
    }
}

/* Small Mobile (480px - 767px) */
@media (min-width: 480px) and (max-width: 767px) {
    :root {
        --circle-size: clamp(30px, 6vw, 40px);
        --connector-size: clamp(20px, 4vw, 35px);
        --dot-size: clamp(10px, 4vw, 10px);
    }

    .date-timeline-container {
        gap: var(--spacing-sm);
        min-height: 50px;
    }

    .date-timeline-step {
        min-width: clamp(60px, 10vw, 80px);
    }

    .date-timeline-date {
        /* Hide original text content on small mobile */
        font-size: 0;
        line-height: 0;
    }

    .date-timeline-date::before {
        /* Show data-short attribute value instead */
        content: attr(data-short);
        font-size: var(--font-size-sm);
        line-height: 1.3;
        color: var(--color-primary);
        font-weight: var(--font-weight-medium);
    }
}

/* Tablet (768px - 1023px) */
@media (min-width: 768px) and (max-width: 1023px) {
    :root {
        --circle-size: clamp(40px, 6vw, 55px);
        --connector-size: clamp(50px, 8vw, 70px);
        --dot-size: clamp(10px, 4vw, 10px);
    }

    .date-timeline-container {
        gap: var(--spacing-md);
        min-height: 60px;
    }
}

/* Desktop (1024px and up) */
@media (min-width: 1024px) {
    :root {
        --circle-size: clamp(50px, 5vw, 70px);
        --connector-size: clamp(70px, 8vw, 120px);
        --dot-size: clamp(10px, 3vw, 10px);
    }

    .installments.payment-progress {
        max-width: 800px;
        padding: var(--spacing-xl);
    }

    .date-timeline-container {
        gap: var(--spacing-lg);
        min-height: 70px;
    }
}

/* ===== ACCESSIBILITY & FOCUS STATES ===== */
.installments-step:focus-visible,
.date-timeline-step:focus-visible {
    outline: 3px solid var(--color-primary);
    outline-offset: 2px;
    border-radius: var(--border-radius);
}

.modern-timeline-dot:focus-visible {
    outline: 3px solid var(--color-primary);
    outline-offset: 3px;
    border-radius: var(--border-radius);
}

@media (pointer: coarse) {
    .installments-step,
    .date-timeline-step {
        min-height: var(--touch-target-min);
        min-width: var(--touch-target-min);
        padding: var(--spacing-sm);
    }

    .modern-timeline-dot {
        min-width: var(--touch-target-small);
        min-height: var(--touch-target-small);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .date-timeline-container::before {
        top: calc(var(--dot-size) / 1.2 - var(--timeline-line-height) / 2);
    }

    .date-timeline.has-hidden-installments .date-timeline-container::after {
        top: calc(var(--dot-size) / 1.2 - var(--timeline-line-height) / 2);
    }
}

@media (prefers-reduced-motion: reduce) {
    .date-timeline.has-hidden-installments .date-timeline-container::after {
        animation: none;
    }
}

/* ===== THEME SUPPORT ===== */

/* High Contrast Mode */
@media (prefers-contrast: high) {
    :root {
        --color-primary: #000000;
        --color-border: #000000;
        --shadow-sm: none;
        --shadow-md: none;
        --shadow-lg: none;
    }

    .installments.payment-progress.bordered,
    .installments.payment-progress.date-timeline.bordered {
        border: 2px solid #000000;
        box-shadow: none;
    }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }

    .installments-step:hover,
    .date-timeline-step:hover {
        transform: none;
    }
}

/* Print Styles */
@media print {
    .installments.payment-progress {
        box-shadow: none;
        border: 1px solid #000;
        background: white !important;
        color: black !important;
        max-width: 100%;
        padding: 1cm;
    }

    .modern-timeline-dot,
    .installments-circle-container {
        box-shadow: none;
        border: 2px solid black;
    }
}